import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Network } from 'lucide-react';
import { hadithApi } from '../services/api';
import { Button } from '../components/ui/button';
import { PageLoading, GlassCard } from '../components/shared';

const PAGE_SIZE = 25;

const HadithEntries = () => {
  const { t } = useTranslation();
  const { slug, bookNumber } = useParams();
  const [book, setBook] = useState(null);
  const [collection, setCollection] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      hadithApi.getCollection(slug),
      hadithApi.getBook(slug, bookNumber),
      hadithApi.getEntries(slug, bookNumber, { skip, limit: PAGE_SIZE }),
    ])
      .then(([c, b, e]) => {
        setCollection(c.data);
        setBook(b.data);
        setItems(e.data.items);
        setTotal(e.data.total);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug, bookNumber, skip]);

  if (loading) return <PageLoading />;
  if (!book) return null;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  return (
    <div className="min-h-screen py-8 px-4" data-testid="hadith-entries-page">
      <div className="max-w-5xl mx-auto">
        <Link
          to={`/hadith/${slug}`}
          className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-primary mb-6"
          data-testid="hadith-back-to-books"
        >
          <ChevronLeft className="w-4 h-4" /> {t('hadith.books_of', { name: collection?.name_fr || collection?.name_en })}
        </Link>

        <div className="mb-8">
          <span className="overline">{t('hadith.book_label', { number: book.book_number })}</span>
          <p className="font-arabic text-3xl text-text-primary text-right mt-1" dir="rtl">
            {book.name_ar}
          </p>
          <h1 className="text-2xl sm:text-3xl font-serif text-text-primary mt-2">{book.name_en}</h1>
          <p className="text-sm text-text-muted mt-1">
            {t('hadith.hadiths_count', { count: total.toLocaleString() })} · {t('common.page_of', { current: currentPage, total: totalPages })}
          </p>
        </div>

        <div className="space-y-4">
          {items.map((h, i) => (
            <motion.div
              key={h.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: Math.min(i * 0.02, 0.25) }}
            >
              <Link to={`/hadith/entry/${h.id}`} data-testid={`hadith-entry-card-${h.id_in_book}`}>
                <GlassCard className="hover:border-primary/40 transition-all cursor-pointer">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-mono text-primary">
                      #{h.id_in_book}
                    </span>
                    {h.has_sanad_data && (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-400 bg-emerald-900/30 px-2 py-0.5 rounded">
                        <Network className="w-3 h-3" /> {t('hadith.sanad_available')}
                      </span>
                    )}
                  </div>
                  {h.narrator_english && (
                    <p className="text-sm italic text-text-secondary mb-2">{h.narrator_english}</p>
                  )}
                  <p className="font-arabic text-lg text-text-primary text-right leading-loose line-clamp-3" dir="rtl">
                    {h.preview_ar}…
                  </p>
                  {h.preview_en && (
                    <p className="text-sm text-text-secondary mt-3 line-clamp-3">{h.preview_en}</p>
                  )}
                </GlassCard>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-8">
          <Button
            variant="outline"
            size="sm"
            disabled={skip === 0}
            onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
            data-testid="hadith-entries-prev"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> {t('common.previous')}
          </Button>
          <span className="text-sm text-text-muted">
            {t('common.page_of', { current: currentPage, total: totalPages })}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={skip + PAGE_SIZE >= total}
            onClick={() => setSkip(skip + PAGE_SIZE)}
            data-testid="hadith-entries-next"
          >
            {t('common.next')} <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HadithEntries;
